import React, { useState, useEffect, useCallback } from 'react';
import {
  SignatureViewProps,
  GetSignatureResponse,
  UpdateSignatureResponse,
  AgentEvents,
  ProfileSignatureUpdatedEvent,
} from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import WysiwygEditor from '../../../common/WysiwygEditor';
import { usePusher } from '../../../../contexts/PusherContext';
import './signature-view.scss';

interface SignatureViewComponentProps extends SignatureViewProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const SignatureView: React.FC<SignatureViewComponentProps> = ({
  profileIds = [],
  oauthIds = [],
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const [activeProfileIndex, setActiveProfileIndex] = useState<number>(0);
  const [signatures, setSignatures] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [profileInfos, setProfileInfos] = useState<
    Record<number, { name: string; email: string }>
  >({});
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);

  const activeProfileId = profileIds[activeProfileIndex];

  // Fetch signatures for all profiles
  const fetchSignatures = useCallback(async () => {
    if (!profileIds || profileIds.length === 0) {
      setError('No profile IDs provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const newSignatures: Record<number, string> = {};
      const newProfileInfos: Record<number, { name: string; email: string }> =
        {};

      for (const profileId of profileIds) {
        try {
          const response = await api.get<GetSignatureResponse>(
            `/signatures/${profileId}`,
          );

          if (response.data.success) {
            newSignatures[profileId] = response.data.signature || '';
            if (response.data.profile) {
              newProfileInfos[profileId] = {
                name: response.data.profile.name,
                email: response.data.profile.email,
              };
            }
          }
        } catch (profileErr) {
          console.error(
            `Error fetching signature for profile ${profileId}:`,
            profileErr,
          );
        }
      }

      setSignatures(newSignatures);
      setProfileInfos(newProfileInfos);
    } catch (err) {
      console.error('Error fetching signatures:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load signatures',
      );
    } finally {
      setLoading(false);
    }
  }, [profileIds, getAccessTokenSilently]);

  // Fetch signatures on mount
  useEffect(() => {
    fetchSignatures();
  }, [fetchSignatures]);

  // Listen for Pusher events for signature updates
  useEffect(() => {
    if (!pusherChannel || !profileIds || profileIds.length === 0) return;

    const handleSignatureUpdated = (data: ProfileSignatureUpdatedEvent) => {
      // Only refetch if the updated profile is one of ours
      if (profileIds.includes(data.profileId)) {
        console.log(
          `Signature updated for profile ${data.profileId}, refetching...`,
        );
        fetchSignatures();
      }
    };

    // Bind to the signature update event
    pusherChannel.bind(
      AgentEvents.PROFILE_SIGNATURE_UPDATED,
      handleSignatureUpdated,
    );

    return () => {
      pusherChannel.unbind(
        AgentEvents.PROFILE_SIGNATURE_UPDATED,
        handleSignatureUpdated,
      );
    };
  }, [pusherChannel, profileIds, fetchSignatures]);

  const handleSave = async () => {
    if (!activeProfileId) {
      setError('No active profile selected');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.put<UpdateSignatureResponse>(
        `/signatures/${activeProfileId}`,
        { signature: signatures[activeProfileId] || '' },
      );

      if (!response.data.success) {
        setError(response.data.error || 'Failed to save signature');
      }
    } catch (err) {
      console.error('Error saving signature:', err);
      setError(err instanceof Error ? err.message : 'Failed to save signature');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadFile = async (file: File) => {
    if (!activeProfileId) {
      setError('No active profile selected');
      return;
    }

    try {
      setUploadingFile(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Step 1: Get presigned URL for upload
      const fileExtension = file.name.split('.').pop() || 'eml';
      const presignedResponse = await api.post(
        '/agent-files/presigned-upload',
        {
          fileExtension,
        },
      );

      if (!presignedResponse.data.success) {
        setError('Failed to get upload URL');
        return;
      }

      // Step 2: Upload file to S3 using presigned URL
      const formData = new FormData();
      Object.keys(presignedResponse.data.fields).forEach((key) => {
        formData.append(key, presignedResponse.data.fields[key]);
      });
      formData.append('file', file);

      const uploadedFileUrl = await fetch(presignedResponse.data.url, {
        method: 'POST',
        body: formData,
      });

      // Step 3: Process the uploaded file
      const processResponse = await api.post(`/signatures/process-file`, {
        fileUrl: uploadedFileUrl.url,
        profileId: activeProfileId,
      });

      if (processResponse.data.success) {
        setSignatures((prev) => ({
          ...prev,
          [activeProfileId]: processResponse.data.signature || '',
        }));
      } else {
        setError(
          processResponse.data.error || 'Failed to process signature file',
        );
      }
    } catch (err) {
      console.error('Error uploading signature file:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to upload signature file',
      );
    } finally {
      setUploadingFile(false);
    }
  };

  const activeProfileInfo = activeProfileId
    ? profileInfos[activeProfileId]
    : null;
  const activeSignature = activeProfileId
    ? signatures[activeProfileId] || ''
    : '';

  if (profileIds.length === 0) {
    return (
      <div className='signature-view'>
        <h2>Email Signatures</h2>
        You haven't set up any profiles yet (these are the email accounts your
        Dreamie will send email from). Ask your Dreamie to do so and you'll be
        able to edit their signatures.
      </div>
    );
  }

  if (loading) {
    return <div className='signature-view-loading'>Loading signatures...</div>;
  }

  if (error && Object.keys(signatures).length === 0) {
    return <div className='signature-view-error'>Error: {error}</div>;
  }

  return (
    <div className='signature-view'>
      <div className='signature-view-header'>
        <h2>Email Signatures</h2>
      </div>

      {profileIds.length > 0 ? (
        <div className='signature-view-profile-selector'>
          <select
            id='profile-select'
            value={activeProfileIndex}
            onChange={(e) => setActiveProfileIndex(Number(e.target.value))}
            className='signature-view-profile-dropdown'
          >
            {profileIds.map((profileId: number, index: number) => (
              <option key={profileId} value={index}>
                {profileInfos[profileId]?.name || `Profile ${profileId}`}
                {profileInfos[profileId]?.email
                  ? ` (${profileInfos[profileId].email})`
                  : ''}
              </option>
            ))}
          </select>
        </div>
      ) : activeProfileInfo ? (
        <div className='signature-view-profile-info'>
          {activeProfileInfo.name} - {activeProfileInfo.email}
        </div>
      ) : null}

      <div className='signature-view-editor'>
        <WysiwygEditor
          value={activeSignature}
          onChange={(value) => {
            if (activeProfileId) {
              setSignatures((prev) => ({
                ...prev,
                [activeProfileId]: value,
              }));
            }
          }}
          placeholder='Enter your email signature...'
        />
      </div>

      {error && <div className='signature-view-error-message'>{error}</div>}

      <div className='signature-view-actions'>
        <span>Signature Actions:</span>
        <div className='actions-wrapper'>
          <button
            onClick={handleSave}
            disabled={saving || uploadingFile}
            className='signature-view-save-button'
          >
            {saving ? 'Saving...' : 'Save Signature'}
          </button>

          <label className='signature-view-upload-button'>
            <input
              type='file'
              accept='.eml'
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUploadFile(file);
              }}
              disabled={saving || uploadingFile}
              style={{ display: 'none' }}
            />
            {uploadingFile
              ? 'Processing (can take time)...'
              : 'Upload email sample'}
          </label>
        </div>
      </div>
      <p className='sig-tip'>
        Tip: Ask in chat how to add a signature from an existing email.
      </p>
    </div>
  );
};

export default SignatureView;
